import { ChooseFileUploadModal } from '@components/ChooseMediaModal/ChooseFileUploadModal';
import { ChooseFileUploadModalSummaryConfirm } from '@components/ChooseMediaModal/ChooseFileUploadModalSummaryConfirm';
import { DragDropOverlay } from '@components/GlobalDragDropOverlay';
import { NoResultForFilter } from '@components/NoResultForFilter/NoResultForFilter';
import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { buildSkeletonPattern, MIN_CELL_WIDTH } from '@components/VirtualMasonry/masonryConstants';
import {
  VirtualMasonry,
  VirtualMasonryCellProps,
  VirtualMasonryRef,
} from '@components/VirtualMasonry/VirtualMasonry';
import { VirtualMasonryGridCellSkeleton } from '@components/VirtualMasonry/VirtualMasonryGridCellSkeleton/VirtualMasonryGridCellSkeleton';
import { DragDropRejected } from '@lib/files/drag-files';
import { CenteredCardProvider } from '@lib/hooks/CenteredCardContext';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { useDragDropUpload } from '@lib/hooks/useDragDropUpload';
import { useFileUploadFlow } from '@lib/hooks/useFileUploadFlow';
import { useAddVideosModal } from '@pages/Library/context/AddVideosModalContext';
import { useMediaStatusPolling } from '@pages/Library/hooks/useMediaStatusPolling';
import { isProbablyImage } from '@pages/Library/utils/mediaStatus';
import { matchesOmniFilter, parseOmniQuery } from '@pages/Library/utils/omniFilter';
import { useLibrary } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useModalStore } from '@stores/ModalStore';
import { omniFilterPreferences } from '@stores/OmniFilterPreferences';
import { FileUploadStatus, useUploadManager } from '@stores/UploadManager/UploadManager';
import { Media } from '@typings/Media.js';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyFolder, EmptyLibrary } from '../EmptyLibrary/EmptyLibrary';
import { MediaDragSource } from '../Folders/MediaDragSource';
import { MediaCard } from '../MediaCard/MediaCard';
import css from './LibraryContentMediaList.module.scss';
import { VirtualUploadCard } from './VirtualUploadCard';

const UI_HIGHLIGHT_DURATION_MS = 2000;

const MOBILE_MIN_CELL_WIDTH = 50;
const MOBILE_CELL_GAP = 8;

export const LibraryContentMediaList = observer(() => {
  const isMobile = useIsMobile();
  const library = useLibrary();
  const folder = useLibraryFolder();
  const modalStore = useModalStore();
  const containerRef = useRef<HTMLDivElement>(null);
  // Ref for the scrollable content inside GradientScrollDiv
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const masonryRef = useRef<VirtualMasonryRef>(null);
  const [masonryWidth, setMasonryWidth] = useState(0);
  const uploadManager = useUploadManager();
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightedMediaId, setHighlightedMediaId] = useState<string | null>(null);

  // Track visible cell indices for the periodic refresh hook
  const visibleCellIndicesRef = useRef<number[]>([]);

  // Cache client-side dimensions by media ID so they survive upload status cleanup.
  // Upload entries are cleared once the backend confirms the file, but backend dimensions
  // aren't available until processing finishes — this bridges that gap.
  // NOTE: This map grows without bound (entries are never pruned). Each entry is ~50 bytes,
  // so even hundreds of uploads per session are negligible. Not worth the complexity of
  // pruning against allMediaItems on every render.
  const clientDimensionsRef = useRef<Map<string, { width: number; height: number }>>(new Map());

  // State for file upload modal
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  // Drag-drop validation rejects from the current drop gesture. Stamped with the
  // batch notificationId inside uploadFiles if the user confirms; if the user
  // dismisses the modal, pushed into rejectedFiles untagged (library-scoped).
  const [droppedRejected, setDroppedRejected] = useState<DragDropRejected[]>([]);
  const droppedRejectedRef = useRef(droppedRejected);
  droppedRejectedRef.current = droppedRejected;

  const addVideosModal = useAddVideosModal();

  const fileUploadFlow = useFileUploadFlow({
    files: droppedFiles,
    onConfirm: async (selectedFiles) => {
      if (folder?.library?.libraryId) {
        const rejects = droppedRejectedRef.current;
        setDroppedFiles([]);
        setDroppedRejected([]);
        addVideosModal?.closeAddVideosModal();
        uploadManager
          .uploadFiles(
            selectedFiles,
            {
              libraryId: folder.library.libraryId,
              folderPath: folder.currentFolderPath,
            },
            'drag-drop',
            rejects,
          )
          .then(() => uploadManager.refreshLibrary(folder.library.libraryId))
          .catch((error) => console.error('Error uploading files:', error));
      }
    },
    onDismiss: () => {
      // User bailed on the upload modal — push unclaimed rejects library-scoped
      // so they still surface in the failed-uploads list (no batch to tag).
      const unclaimed = droppedRejectedRef.current;
      if (unclaimed.length > 0) {
        uploadManager.rejectedFiles.push(
          ...unclaimed.map((r) => ({
            file: r.file,
            reason: r.reason,
            category: 'other' as const,
            status: 'failed' as const,
          })),
        );
      }
      setDroppedFiles([]);
      setDroppedRejected([]);
    },
  });

  // Handle dropped files by opening the file upload modal.
  // Appends to existing selection and dedupes by name+size+lastModified.
  // Skip if the add-media modal is open — MediaUploadBox handles drops in that case.
  // Refs keep the callback stable (empty deps) so useDragDropUpload doesn't re-register listeners.
  const modalStoreRef = useRef(modalStore);
  modalStoreRef.current = modalStore;
  const fileUploadFlowOpenRef = useRef(fileUploadFlow.open);
  fileUploadFlowOpenRef.current = fileUploadFlow.open;
  const handleFilesDropped = useCallback((files: File[], rejected: DragDropRejected[]) => {
    // Anywhere in the stack, not just top-most: the file picker legitimately opens on top of
    // add-media, and an exact top-of-stack check would stop matching there and let this handle
    // the drop as well as MediaUploadBox.
    if (modalStoreRef.current.openModalIds.includes('add-media')) return;

    setDroppedFiles((prev) => {
      const existingKeys = new Set(prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
      const newFiles = files.filter(
        (f) => !existingKeys.has(`${f.name}-${f.size}-${f.lastModified}`),
      );
      return [...prev, ...newFiles];
    });
    if (rejected.length > 0) {
      setDroppedRejected((prev) => [...prev, ...rejected]);
    }
    queueMicrotask(() => fileUploadFlowOpenRef.current());
  }, []);

  // Drag and drop functionality with custom hook (overlay only on drag, no modal)
  const { isDragging } = useDragDropUpload(
    true, // Enable mouse leave handler
    undefined, // No modal on drag enter
    undefined,
    undefined,
    handleFilesDropped,
  );

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.getBoundingClientRect().width;
        setMasonryWidth(isMobile ? containerWidth : containerWidth - 20);
      }
    };

    // Initial calculation
    updateWidth();

    // Create ResizeObserver for more reliable size updates
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
    // containerRef is intentionally omitted — it's a ref and doesn't change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Cancel upload: immediately clear and remove from list (no confirmation modal).
  // clearAndCleanupUploads aborts the upload, deletes backend media if created, clears state, and refreshes.
  const handleUploadClearing = (status: FileUploadStatus) => {
    uploadManager
      .clearAndCleanupUploads([status.uuid], {
        folderStore: folder,
        mode: 'individual',
        showSnackbar: true,
      })
      .catch((err) => {
        console.error('Cancel upload failed:', err);
      });
  };

  // Get active uploads for the open folder (same logic as table view useMediaData)
  // Include 'success' so we keep showing the card until folder.files has it (avoids disappear-reappear jank)
  const activeUploadStatuses = Object.values(uploadManager.uploadStatus).filter(
    (s) =>
      (s.status === 'queued' || s.status === 'uploading' || s.status === 'success') &&
      folder.holdsUpload(s.destination),
  );

  // Only dedupe virtual upload when backend media has moved past "empty" (avoids empty blip while upload finishes)
  const existingNonEmptyMediaIds = new Set(
    folder.files.filter((f) => f.status !== 'empty').map((f) => f._id),
  );

  // Build all media items (uploads + existing) to sort together
  type MediaItem =
    | (Media & { isUpload: false; uploadStatus?: FileUploadStatus })
    | {
        _id: string;
        createdAt: string;
        isUpload: true;
        uploadStatus: FileUploadStatus;
        metadata: {
          name: string;
          video: { duration: number; width: number; height: number };
        };
      };

  // Parse the omnifilter once; reused for the virtual upload rows below. (folder.files
  // is already omni-filtered by the store; virtual uploads aren't in folder.files yet.)
  const parsedOmni = parseOmniQuery(folder._omniFilter, omniFilterPreferences.matchAll);

  const allMediaItems: MediaItem[] = [
    // Virtual upload items (not yet in folder.files)
    ...activeUploadStatuses
      .filter((status) => {
        // Don't include virtual upload only when backend media exists AND has moved past "empty"
        if (status.media?._id && existingNonEmptyMediaIds.has(status.media._id)) {
          return false;
        }
        // Apply the same omnifilter used on folder.files. Uploads only have a filename
        // (and maybe a backend id), so id/YT-id tokens just won't match — title tokens do.
        if (
          parsedOmni &&
          !matchesOmniFilter({ name: status.file.name, mediaId: status.media?._id }, parsedOmni)
        ) {
          return false;
        }
        return true;
      })
      .map((status) => ({
        _id: status.media?._id ?? status.uuid,
        createdAt: status.createdAt, // Use stable timestamp from when queued
        isUpload: true as const,
        uploadStatus: status,
        metadata: {
          name: status.file.name,
          video: { duration: 0, width: 0, height: 0 },
        },
      })),
    // Existing media items: always hide empty; uploading card is shown via virtual upload item
    ...folder.files
      .filter((file) => file.status !== 'empty')
      .map((file) => ({
        ...file,
        isUpload: false as const,
        uploadStatus: uploadManager.uploadStatus[uploadManager.mediaIdToLocalUUID[file._id]],
      })),
  ];

  // Sort according to folder sort model (folder.files is already sorted, but we need to re-sort the merged list)
  const sortModel = folder.effectiveSortModel;
  if (sortModel.length > 0) {
    const { field, sort: sortDirection } = sortModel[0];
    const direction = sortDirection === 'asc' ? 1 : -1;

    allMediaItems.sort((a, b) => {
      let valA: string | number | null = null;
      let valB: string | number | null = null;

      switch (field) {
        case 'filename':
          valA = a.isUpload ? a.metadata.name : (a.metadata?.name ?? a.metadata?.filename ?? null);
          valB = b.isUpload ? b.metadata.name : (b.metadata?.name ?? b.metadata?.filename ?? null);
          break;
        case 'duration':
          valA = !a.isUpload && isProbablyImage(a) ? null : (a.metadata?.video?.duration ?? null);
          valB = !b.isUpload && isProbablyImage(b) ? null : (b.metadata?.video?.duration ?? null);
          break;
        case 'createdAt':
          valA = a.createdAt ?? null;
          valB = b.createdAt ?? null;
          break;
        case 'updatedAt':
          valA = a.isUpload ? null : (a.updatedAt ?? null);
          valB = b.isUpload ? null : (b.updatedAt ?? null);
          break;
        case 'publishedAt':
          valA = a.isUpload ? null : (a.publishedAt ?? null);
          valB = b.isUpload ? null : (b.publishedAt ?? null);
          break;
        default:
          return 0;
      }

      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      const cmp =
        typeof valA === 'string'
          ? valA.localeCompare(valB as string)
          : (valA as number) - (valB as number);

      return cmp * direction;
    });
  }

  // Populate the client-dimensions cache before cell mapping.
  // This is an explicit pre-computation step (not a side-effect inside .map()).
  // Cache entries survive upload status cleanup so card shape doesn't jump during processing.
  for (const item of allMediaItems) {
    const id = item._id;
    const clientW = item.isUpload ? item.uploadStatus.width : item.uploadStatus?.width;
    const clientH = item.isUpload ? item.uploadStatus.height : item.uploadStatus?.height;
    if (clientW && clientH) {
      clientDimensionsRef.current.set(id, { width: clientW, height: clientH });
    }
  }

  // Pure card-shape computation — reads from cache but does not mutate it.
  // Nearly-square (ratio in [0.85, 1.15]) is explicitly short so it renders
  // in the near-square slot rather than the 16:9 tall slot, which fits the
  // content better in portrait-majority layouts.
  const getCardShapeForMedia = (
    mediaId: string,
    backendW: number | undefined,
    backendH: number | undefined,
    clientW: number | undefined,
    clientH: number | undefined,
  ): 'tall' | 'short' => {
    const w = backendW || clientW || clientDimensionsRef.current.get(mediaId)?.width;
    const h = backendH || clientH || clientDimensionsRef.current.get(mediaId)?.height;
    if (!w || !h) return 'short';
    const ratio = w / h;
    if (ratio >= 0.85 && ratio <= 1.15) return 'short';
    return h > w ? 'tall' : 'short';
  };

  // On mobile, force all cards to short so uniformSpan=4 keeps the layout narrow.
  //
  // This does NOT produce one card per row — it only fixes uniformSpan, and the column count is
  // still derived from the width, so with MOBILE_MIN_CELL_WIDTH/MOBILE_CELL_GAP it reaches 2 slots
  // at 464px and 3 at 768px (an iPad in portrait is on this branch). Don't build anything on a
  // single-column assumption here.
  const getEffectiveCardShape = (
    mediaId: string,
    backendW: number | undefined,
    backendH: number | undefined,
    clientW: number | undefined,
    clientH: number | undefined,
  ): 'tall' | 'short' => {
    if (isMobile) return 'short';
    return getCardShapeForMedia(mediaId, backendW, backendH, clientW, clientH);
  };

  // Convert to cells
  const cells = allMediaItems.map((item) => {
    if (item.isUpload) {
      const status = item.uploadStatus;
      // Show completed card from status.media until folder.files has it (avoids disappear-reappear jank)
      if (status.status === 'success' && status.media) {
        const media = status.media;
        const isHighlighted = highlightedMediaId === media._id;
        const cardShape = getEffectiveCardShape(
          media._id,
          media.metadata?.video?.width,
          media.metadata?.video?.height,
          status.width,
          status.height,
        );
        return {
          key: `media-${media._id}`,
          cardShape,
          isHighlighted,
          children: ({ isVisible, width: cellWidth }: VirtualMasonryCellProps) => (
            <MediaDragSource mediaId={media._id}>
              <MediaCard
                file={media}
                fileUploadStatus={status}
                isVisible={isVisible}
                isHighlighted={isHighlighted}
                cellWidth={cellWidth}
              />
            </MediaDragSource>
          ),
        };
      }
      const uploadMediaId = status.media?._id ?? status.uuid;
      const uploadCardShape = getEffectiveCardShape(
        uploadMediaId,
        undefined,
        undefined,
        status.width,
        status.height,
      );
      return {
        key: `upload-${status.media?._id ?? status.uuid}`,
        cardShape: uploadCardShape,
        children: ({ isVisible, width: cellWidth }: VirtualMasonryCellProps) => (
          <VirtualUploadCard
            status={status}
            isVisible={isVisible}
            cellWidth={cellWidth}
            onClear={() => handleUploadClearing(status)}
          />
        ),
      };
    } else {
      const file = item;
      const isHighlighted = highlightedMediaId === file._id;
      const cardShape = getEffectiveCardShape(
        file._id,
        file.metadata?.video?.width,
        file.metadata?.video?.height,
        item.uploadStatus?.width,
        item.uploadStatus?.height,
      );
      return {
        key: `media-${file._id}`,
        cardShape,
        isSelected: folder.videoSelection.isSelected(file._id),
        isHighlighted,
        children: ({ isVisible, width: cellWidth }: VirtualMasonryCellProps) => (
          <MediaDragSource mediaId={file._id}>
            <MediaCard
              file={file}
              fileUploadStatus={item.uploadStatus}
              isVisible={isVisible}
              isHighlighted={isHighlighted}
              cellWidth={cellWidth}
            />
          </MediaDragSource>
        ),
      };
    }
  });

  // Status polling is folder-level: one request covers every media here, so
  // there is no longer a per-visible-cell request set to compute.
  useMediaStatusPolling();

  // Update visible cell indices when visibility changes (includes overscan for rendering)
  const handleVisibleCellsChange = useCallback((visibleCellIndices: number[]) => {
    visibleCellIndicesRef.current = visibleCellIndices;
  }, []);

  // Function to scroll to a specific media ID
  const scrollToMediaId = useCallback(
    (mediaId: string, behavior: ScrollBehavior = 'smooth') => {
      if (!masonryRef.current) return;

      // Find the index of the cell with the matching media ID
      const cellIndex = cells.findIndex((cell) => cell.key === `media-${mediaId}`);
      if (cellIndex === -1) return;

      masonryRef.current.scrollToIndex(cellIndex, behavior);
    },
    [cells],
  );

  // Handle URL query parameter to scroll to specific media
  useEffect(() => {
    const scrollToParam = searchParams.get('mediaId');
    if (
      scrollToParam &&
      cells.length > 0 &&
      masonryRef.current &&
      containerRef.current &&
      scrollContainerRef.current
    ) {
      // Wait a bit for the masonry to be fully rendered
      const timeoutId = setTimeout(() => {
        const container = containerRef.current;
        const scrollContainer = scrollContainerRef.current;
        if (!container || !scrollContainer) return;

        // Find the cell index for this media ID
        const cellIndex = cells.findIndex((cell) => cell.key === `media-${scrollToParam}`);
        if (cellIndex === -1) return;

        // Find the cell element by index (all cells have data-highlighted attribute)
        const allCells = container.querySelectorAll('[data-highlighted]');
        const targetCell = allCells[cellIndex] as HTMLElement | undefined;

        let scrollBehavior: ScrollBehavior = 'instant';
        let shouldScroll = true;

        if (targetCell) {
          const cellRect = targetCell.getBoundingClientRect();
          const scrollRect = scrollContainer.getBoundingClientRect();

          // Check if cell is completely visible within the scrollable viewport
          const isCompletelyVisible =
            cellRect.top >= scrollRect.top &&
            cellRect.bottom <= scrollRect.bottom &&
            cellRect.left >= scrollRect.left &&
            cellRect.right <= scrollRect.right;

          // Check if cell is partially visible
          const isPartiallyVisible =
            cellRect.top < scrollRect.bottom &&
            cellRect.bottom > scrollRect.top &&
            cellRect.left < scrollRect.right &&
            cellRect.right > scrollRect.left;

          if (isCompletelyVisible) {
            shouldScroll = false;
          } else if (isPartiallyVisible) {
            scrollBehavior = 'smooth';
          }
        }

        if (shouldScroll) {
          scrollToMediaId(scrollToParam, scrollBehavior);
        }

        // Wait for scroll to complete before setting highlight and calculating offset
        // Use requestAnimationFrame to ensure scroll has been applied
        const setHighlight = () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Set highlighted media ID to trigger fade/shrink effect
              setHighlightedMediaId(scrollToParam);
              // Remove the query parameter after scrolling
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.delete('mediaId');
              setSearchParams(newSearchParams, { replace: true });
              // Clear highlight after some seconds
              setTimeout(() => {
                setHighlightedMediaId(null);
              }, UI_HIGHLIGHT_DURATION_MS);
            });
          });
        };

        if (shouldScroll && scrollBehavior === 'smooth') {
          // For smooth scroll, wait longer for it to complete
          setTimeout(setHighlight, 500);
        } else {
          // For instant scroll or no scroll, set highlight immediately
          setHighlight();
        }
      }, 1);

      return () => clearTimeout(timeoutId);
    }
  }, [searchParams, cells.length, scrollToMediaId, setSearchParams]);

  // Calculate and set center offset for highlighted cell
  useEffect(() => {
    if (!highlightedMediaId || !containerRef.current || !scrollContainerRef.current) return;

    const calculateCenterOffset = () => {
      const container = containerRef.current;
      const scrollContainer = scrollContainerRef.current;
      if (!container || !scrollContainer) return;

      const highlightedCell = container.querySelector(`[data-highlighted="true"]`) as HTMLElement;
      if (!highlightedCell) return;

      const cellRect = highlightedCell.getBoundingClientRect();
      const scrollRect = scrollContainer.getBoundingClientRect();

      // Calculate horizontal offset to center in scrollable viewport
      const viewportCenterX = scrollRect.left + scrollRect.width / 2;
      const cellCenterX = cellRect.left + cellRect.width / 2;
      const offsetX = viewportCenterX - cellCenterX;

      // Calculate vertical offset to center in scrollable viewport
      const viewportCenterY = scrollRect.top + scrollRect.height / 2;
      const cellCenterY = cellRect.top + cellRect.height / 2;
      const offsetY = viewportCenterY - cellCenterY;

      highlightedCell.style.setProperty('--center-offset-x', `${offsetX}px`);
      highlightedCell.style.setProperty('--center-offset-y', `${offsetY}px`);
    };

    // Wait for scroll to complete and cell to be rendered before calculating
    // Use multiple requestAnimationFrame calls to ensure DOM has updated
    const calculateAfterScroll = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          calculateCenterOffset();
        });
      });
    };

    // Calculate after a delay to ensure everything is rendered
    const timeoutId = setTimeout(calculateAfterScroll, 1);

    // Also recalculate on window resize and scroll
    window.addEventListener('resize', calculateCenterOffset);
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', calculateCenterOffset);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateCenterOffset);
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', calculateCenterOffset);
      }
    };
  }, [highlightedMediaId]);

  if (!folder) return null;

  // Only once the listing has answered: without it there is nothing to say the folder is empty,
  // and the skeleton below is the honest state.
  const showsEmptyFolder = cells.length === 0 && folder.data !== null;

  const gridContent = (
    <>
      <GradientScrollDiv className={css.content} ref={scrollContainerRef}>
        <div
          ref={containerRef}
          className={classNames(
            // Center empty states (truly-empty library OR filter-with-no-matches) in
            // the full available height instead of letting them sit at their natural
            // size near the top.
            library.stats?.count == 0 ||
              (cells.length === 0 && (folder._omniFilter || showsEmptyFolder))
              ? css.emptyLibraryContainer
              : '',
          )}
        >
          <DragDropOverlay isDragging={isDragging} />
          {cells.length > 0 ? (
            <VirtualMasonry
              ref={masonryRef}
              width={masonryWidth}
              minCellWidth={isMobile ? MOBILE_MIN_CELL_WIDTH : MIN_CELL_WIDTH}
              masonryCellGap={isMobile ? MOBILE_CELL_GAP : 16}
              className={classNames(
                css.masonry,
                css.fadeIn,
                highlightedMediaId && css.hasHighlight,
              )}
              cells={cells}
              addBorder={false}
              clipCells={false}
              scrollContainerRef={scrollContainerRef}
              onVisibleCellsChange={handleVisibleCellsChange}
              centerCells={isMobile}
            />
          ) : folder._omniFilter ? (
            <NoResultForFilter
              title="No media match your search"
              description="Try other words, or a full YouTube or media ID."
              onClearFilters={() => folder.setOmniFilter(null)}
              clearLabel="Clear search"
            />
          ) : folder.showsEmptyLibrary ? (
            <EmptyLibrary library={library} />
          ) : showsEmptyFolder ? (
            <EmptyFolder library={library} />
          ) : (
            <VirtualMasonry
              ref={masonryRef}
              width={masonryWidth}
              minCellWidth={isMobile ? MOBILE_MIN_CELL_WIDTH : MIN_CELL_WIDTH}
              masonryCellGap={isMobile ? MOBILE_CELL_GAP : 16}
              className={`${css.masonry}`}
              cells={buildSkeletonPattern(
                masonryWidth,
                window.innerHeight,
                isMobile ? MOBILE_MIN_CELL_WIDTH : MIN_CELL_WIDTH,
                isMobile ? MOBILE_CELL_GAP : 16,
              ).map(
                (
                  cardShape,
                  index,
                ): {
                  key: string;
                  cardShape: 'tall' | 'short';
                  children: React.ReactNode;
                } => ({
                  key: `skeleton-${index}`,
                  cardShape,
                  children: <VirtualMasonryGridCellSkeleton />,
                }),
              )}
              addBorder={false}
              centerCells={isMobile}
            />
          )}
        </div>
      </GradientScrollDiv>
      {fileUploadFlow.flowState === 'selecting' && (
        <ChooseFileUploadModal {...fileUploadFlow.selectionProps} open={true} />
      )}
      <ChooseFileUploadModalSummaryConfirm {...fileUploadFlow.summaryProps} />
    </>
  );

  // Always mounted; the provider decides for itself whether the centre line is needed here (only
  // on a device that can't hover) and stays inert otherwise. Same arrangement as MomentGrid. It
  // reveals a whole row at a time, so it needs no help from the column count.
  return <CenteredCardProvider>{gridContent}</CenteredCardProvider>;
});
